import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Mail, MessageCircle, Smartphone } from 'lucide-react';
import { Drawer, Field } from '@/components/live';
import { OTP_FAILURES, otpChannels, sendOtp, verifyOtp, type IssuedOtp, type OtpChannel } from '@/api/otp';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';

const CHANNEL_ICONS = { whatsapp: MessageCircle, sms: Smartphone, email: Mail } as const;
const CHANNEL_NAME = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email' } as const;
type Channel = OtpChannel['channel'];

/**
 * A party confirms something with a one-time code sent to their OWN registered contacts - the
 * same code by each channel ticked - read back to the officer. The code is then handed to
 * `onVerified`, which records what it confirms (an invoice, say) in the database, where the code
 * is checked again against the record and the party. The consent flow has its own (ConsentCapture).
 */
export function OtpCapture({ partyId, linkedTable, linkedId, title, subtitle, intro, onClose, onVerified }: {
  partyId: string;
  linkedTable: string;
  linkedId: string;
  title: string;
  subtitle?: string;
  intro: string;
  onClose: () => void;
  /** Record the confirmation; throw to show the reason and keep the drawer open. */
  onVerified: (otpEventId: string) => Promise<void>;
}) {
  const [selected, setSelected] = useState<Channel[]>([]);
  const [issued, setIssued] = useState<IssuedOtp | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const channels = useQuery({ queryKey: ['otp-channels', partyId], queryFn: () => otpChannels(partyId), enabled: isSupabaseConfigured });
  useEffect(() => { setSelected((channels.data ?? []).filter((row) => row.available).map((row) => row.channel)); }, [channels.data]);

  async function send() {
    setError(undefined);
    const ordered = (['whatsapp', 'sms', 'email'] as Channel[]).filter((entry) => selected.includes(entry));
    if (!ordered[0]) { setError('Tick at least one way to send the code.'); return; }
    setBusy(true);
    try {
      const result = await sendOtp({ purpose: 'acknowledgement', channel: ordered[0], alsoChannels: ordered.slice(1), partyId, linkedTable, linkedId });
      setIssued(result);
      setCode('');
      const went = [`${CHANNEL_NAME[result.channel as Channel] ?? result.channel} (${result.destination_masked})`,
        ...(result.also ?? []).filter((row) => row.delivered).map((row) => `${CHANNEL_NAME[row.channel]} (${row.destination_masked})`)];
      toast.success(`The code was sent by ${went.join(', ')}.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The code could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!issued) return;
    setError(undefined);
    setBusy(true);
    try {
      const result = await verifyOtp(issued.otp_event_id, code);
      if (!result.verified) { setError(OTP_FAILURES[result.failure_reason ?? ''] ?? 'That code could not be verified.'); return; }
      await onVerified(issued.otp_event_id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  const available = channels.data ?? [];
  return (
    <Drawer
      footer={<>
        <button className="cancel" onClick={onClose} type="button">Cancel</button>
        {issued
          ? <button className="primary" disabled={busy || code.length !== 6} onClick={() => void confirm()} type="button">{busy ? 'Checking...' : 'Confirm'}</button>
          : <button className="primary" disabled={busy} onClick={() => void send()} type="button">{busy ? 'Sending...' : 'Send code'}</button>}
      </>}
      onClose={onClose} open subtitle={subtitle} title={title}
    >
      <p className="muted" style={{ margin: 0 }}>{intro}</p>
      <Field label="Send the same code by (tick one or more)">
        <div className="login-channels multi">
          {available.map((entry) => {
            const Icon = CHANNEL_ICONS[entry.channel];
            const on = selected.includes(entry.channel);
            return (
              <button aria-pressed={on} className={on ? 'active' : ''} disabled={!entry.available || Boolean(issued)} key={entry.channel}
                      onClick={() => setSelected((now) => (now.includes(entry.channel) ? now.filter((c) => c !== entry.channel) : [...now, entry.channel]))}
                      title={entry.available ? entry.destination_masked ?? undefined : 'Not recorded for this party'} type="button">
                {on ? <Check size={16} /> : <Icon size={16} />} {CHANNEL_NAME[entry.channel]}
              </button>
            );
          })}
        </div>
      </Field>
      {available.length > 0 && (
        <ul className="otp-destinations">
          {available.map((entry) => (
            <li className={selected.includes(entry.channel) ? '' : 'muted'} key={entry.channel}>
              {CHANNEL_NAME[entry.channel]}: {entry.available ? entry.destination_masked : 'not recorded'}
            </li>
          ))}
        </ul>
      )}
      {issued && (
        <Field label="Code read back" required>
          <input autoFocus className="login-code" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
                 onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} />
        </Field>
      )}
      {error && <p className="login-error" role="alert">{error}</p>}
      {issued && <button disabled={busy} onClick={() => void send()} style={{ justifySelf: 'start' }} type="button">Send a new code</button>}
    </Drawer>
  );
}
